package org.zq.linkgame.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import org.zq.linkgame.ui.components.AppText
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import org.zq.linkgame.ui.components.CircleIconButton
import org.zq.linkgame.ui.components.CloseIcon
import org.zq.linkgame.ui.model.BackgroundTheme
import org.zq.linkgame.ui.model.LocalStrings
import org.zq.linkgame.ui.theme.*

/**
 * Leaderboard data class
 */
data class LeaderboardEntry(
    val playerName: String,
    val score: Int
)

/**
 * Sample leaderboard data
 */
private val sampleLeaderboard = listOf(
    LeaderboardEntry("Player1", 9850),
    LeaderboardEntry("GameMaster", 8720),
    LeaderboardEntry("LinkPro", 7650),
    LeaderboardEntry("Puzzle King", 6890),
    LeaderboardEntry("Star Player", 5430),
    LeaderboardEntry("FastHand", 4980),
    LeaderboardEntry("Newbie", 3210),
    LeaderboardEntry("Challenger", 2560),
    LeaderboardEntry("Lucky", 1890),
    LeaderboardEntry("Beginner", 950)
)

/**
 * Leaderboard screen showing top players
 */
@Composable
fun LeaderboardScreen(
    onBack: () -> Unit,
    backgroundTheme: BackgroundTheme = BackgroundTheme.SKY,
    modifier: Modifier = Modifier
) {
    val strings = LocalStrings.current
    
    BoxWithConstraints(
        modifier = modifier
            .fillMaxSize()
            .background(
                brush = Brush.verticalGradient(
                    colors = listOf(
                        Color(backgroundTheme.topColor),
                        Color(backgroundTheme.bottomColor)
                    )
                )
            )
    ) {
        val isLandscape = maxWidth > maxHeight
        
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(16.dp)
        ) {
            // Top bar
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically
            ) {
                // Back button
                CircleIconButton(
                    onClick = onBack,
                    size = 36.dp,
                    backgroundColor = VeryLightGray
                ) {
                    CloseIcon(size = 18.dp, color = DarkGray)
                }
                
                Spacer(modifier = Modifier.weight(1f))
                
                // Title
                AppText(
                    text = strings.leaderboard,
                    style = MaterialTheme.typography.headlineMedium,
                    color = Color(backgroundTheme.textColor),
                    fontWeight = FontWeight.Bold
                )
                
                Spacer(modifier = Modifier.weight(1f))
                
                // Placeholder for symmetry
                Spacer(modifier = Modifier.size(36.dp))
            }
            
            Spacer(modifier = Modifier.height(16.dp))
            
            // Table header
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(topStart = 12.dp, topEnd = 12.dp))
                    .background(DeepBlue)
                    .padding(horizontal = 16.dp, vertical = 12.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                AppText(
                    text = strings.rank,
                    style = MaterialTheme.typography.titleSmall,
                    color = Color.White,
                    modifier = Modifier.width(60.dp),
                    textAlign = TextAlign.Center
                )
                AppText(
                    text = strings.player,
                    style = MaterialTheme.typography.titleSmall,
                    color = Color.White,
                    modifier = Modifier.weight(1f)
                )
                AppText(
                    text = strings.score,
                    style = MaterialTheme.typography.titleSmall,
                    color = Color.White,
                    modifier = Modifier.width(80.dp),
                    textAlign = TextAlign.End
                )
            }
            
            // Leaderboard list
            LazyColumn(
                modifier = Modifier
                    .fillMaxWidth()
                    .weight(1f)
                    .clip(RoundedCornerShape(bottomStart = 12.dp, bottomEnd = 12.dp))
                    .background(Color.White.copy(alpha = 0.9f))
            ) {
                if (sampleLeaderboard.isEmpty()) {
                    item {
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(32.dp),
                            contentAlignment = Alignment.Center
                        ) {
                            AppText(
                                text = strings.noRecords,
                                style = MaterialTheme.typography.bodyLarge,
                                color = DarkGray
                            )
                        }
                    }
                } else {
                    itemsIndexed(sampleLeaderboard) { index, entry ->
                        LeaderboardRow(
                            rank = index + 1,
                            entry = entry,
                            isEven = index % 2 == 0
                        )
                    }
                }
            }
        }
    }
}

/**
 * Single row in the leaderboard
 */
@Composable
private fun LeaderboardRow(
    rank: Int,
    entry: LeaderboardEntry,
    isEven: Boolean
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(if (isEven) Color.Transparent else LightGray.copy(alpha = 0.3f))
            .padding(horizontal = 16.dp, vertical = 12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Rank with medal for top 3
        Box(
            modifier = Modifier.width(60.dp),
            contentAlignment = Alignment.Center
        ) {
            when (rank) {
                1 -> RankMedal(color = Color(0xFFFFD700), rank = rank) // Gold
                2 -> RankMedal(color = Color(0xFFC0C0C0), rank = rank) // Silver
                3 -> RankMedal(color = Color(0xFFCD7F32), rank = rank) // Bronze
                else -> AppText(
                    text = rank.toString(),
                    style = MaterialTheme.typography.bodyLarge,
                    color = DarkGray,
                    fontWeight = FontWeight.Medium
                )
            }
        }
        
        // Player name
        AppText(
            text = entry.playerName,
            style = MaterialTheme.typography.bodyLarge,
            color = DarkGray,
            modifier = Modifier.weight(1f)
        )
        
        // Score
        AppText(
            text = entry.score.toString(),
            style = MaterialTheme.typography.bodyLarge,
            color = DeepBlue,
            fontWeight = FontWeight.Bold,
            modifier = Modifier.width(80.dp),
            textAlign = TextAlign.End
        )
    }
}

/**
 * Medal icon for top 3 ranks
 */
@Composable
private fun RankMedal(
    color: Color,
    rank: Int
) {
    Box(
        modifier = Modifier
            .size(32.dp)
            .clip(CircleShape)
            .background(color),
        contentAlignment = Alignment.Center
    ) {
        AppText(
            text = rank.toString(),
            style = MaterialTheme.typography.bodyMedium,
            color = Color.White,
            fontWeight = FontWeight.Bold
        )
    }
}
